import { Divider, IconButton } from '@mui/joy';
import { PaperPlaneRight } from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Mention, MentionsInput } from 'react-mentions';
import { z } from 'zod';

import { fetchUserList } from '../../../../../services/commentsService';

import { mentionListStyle, mentionStyle } from '~/assets/styles/comments';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { StatusCodeErrorBanner } from '~/services/api/rest-api-error';
import { useForm } from '~/utils/forms';

let container: Element | null = null;

const createCommentFormSchema = z.object({
  referenceId: z.string(),
  comment: z.object({
    value: z.string().trim().min(1),
    mentions: z.array(z.number()),
  }),
  parentId: z.number().optional(),
});

export function CreateCommentForm({
  id,
  defaultValues,
  onCommentCreated: onCommentCreatedHandler,
  onCommentDiscarded: onCommentDiscardedHandler,
}: {
  id?: string;
  defaultValues?: Partial<z.infer<typeof createCommentFormSchema>>;
  onCommentCreated?: () => void;
  onCommentDiscarded?: () => void;
}) {
  const intl = useIntl();

  const form = useForm({
    schema: createCommentFormSchema,
    defaultValues: {
      comment: {
        mentions: [],
        value: '',
      },
      ...defaultValues,
    },
  });

  const createComment = useWrappedMutation(
    ReportService.endpoints.createComment.useMutation(),
  );
  const onSubmitHandler = form.handleSubmit(async (values) => {
    await createComment.mutateAsync(
      {
        referenceId: values.referenceId,
        comment: {
          content: values.comment.value,
          tagged_users: values.comment.mentions,
          parent: values.parentId,
        },
      },
      {
        onSuccess: () => {
          form.reset();
          if (onCommentCreatedHandler) {
            onCommentCreatedHandler();
          }
        },
      },
    );
  });

  const isSubmitting = form.formState.isSubmitting;
  const formId = useId(id);

  return (
    <Card size='sm'>
      <div>
        <StatusCodeErrorBanner error={createComment.state.error} />
        <form
          id={formId}
          onSubmit={onSubmitHandler}
          ref={(el) => {
            container = el;
          }}
        >
          <Controller
            control={form.control}
            name='comment'
            render={({ field }) => (
              <div className='h-20 overflow-y-auto'>
                <MentionsInput
                  ref={field.ref}
                  onChange={(_, newValue, __, mentionList) => {
                    field.value = {
                      value: newValue,
                      mentions: mentionList.map((mention) => {
                        return Number(mention.id);
                      }),
                    };
                    field.onChange(field.value);
                  }}
                  onBlur={field.onBlur}
                  value={field.value.value}
                  rows={5}
                  cols={5}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Start commenting and tag others with @...',
                  })}
                  suggestionsPortalHost={container ?? undefined}
                  style={mentionListStyle}
                  allowSuggestionsAboveCursor
                >
                  <Mention
                    trigger='@'
                    data={fetchUserList}
                    style={mentionStyle}
                  />
                </MentionsInput>
              </div>
            )}
          />
        </form>
      </div>
      <Divider />
      <div className='flex flex-col-reverse items-center justify-end gap-1 *:w-full sm:flex-row sm:*:w-auto'>
        <Button
          variant='plain'
          color='neutral'
          size='sm'
          disabled={isSubmitting}
          onClick={() => {
            form.reset();
            if (onCommentDiscardedHandler) {
              onCommentDiscardedHandler();
            }
          }}
        >
          <FormattedMessage defaultMessage='Cancel' />
        </Button>
        <IconButton
          form={formId}
          color='secondary'
          variant='solid'
          size='sm'
          type='submit'
          disabled={isSubmitting}
          sx={{ borderRadius: '999px' }}
        >
          <PaperPlaneRight weight='fill' className='w-4 h-4' />
        </IconButton>
      </div>
    </Card>
  );
}
